/**
 * «Fechar negócio» — a única forma de marcar uma proposta como ganha (o
 * seletor de estado abre este diálogo em vez de mudar o estado). Pede:
 * dados do cliente + NIF (obrigatórios, como o diálogo do Odoo), relatório
 * (obrigatório), fatura de eletricidade (obrigatória só no solar) e fotos da
 * obra (opcionais, até 10). Gera o PDF da proposta, sobe tudo ao host e chama
 * `win_proposal`, que cria a encomenda e leva relatório, PDF e ficheiros para
 * o negócio no Odoo.
 *
 * Se o Odoo falhar a meio, o «ganho» fica gravado e «Tentar de novo» volta a
 * chamar com as mesmas chaves (sem voltar a subir nada) — o servidor não
 * duplica anexos nem a nota.
 */
import { useEffect, useState } from "react"
import { CheckCircle2, FileText, ImagePlus, Loader2, Trophy, X } from "lucide-react"
import type { AppShell } from "@/App"
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { shrinkPhoto, uploadAppFile } from "@/lib/app-files"
import { odooOrderLabel } from "@/lib/format"

const NIF_REGEX = /^\d{9}$/
const MAX_PHOTOS = 10
const MAX_BYTES = 14 * 1024 * 1024
const BILL_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"]
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"]

export interface WinClientPrefill {
  name: string
  email: string | null
  phone: string | null
  address: string | null
  city: string | null
  postal_code: string | null
  proposal_number?: string | null
}

interface WonFile { key: string; name: string; mimetype: string }

interface WinResult {
  odoo_order_name: string | null
  odoo_order_id?: number | null
  odoo_lead_id: number | null
  attached: number
  pending_files: string[]
  note_posted: boolean
  odoo_error: string | null
}

export function WinDialog({
  open, onOpenChange, shell, kind, proposalId, client, onWon,
}: {
  open: boolean
  /** Módulo: a fatura de eletricidade só é obrigatória no solar. */
  kind: "solar" | "ac" | "bc_aqs" | "bc_aq"
  onOpenChange: (open: boolean) => void
  shell: AppShell | null
  proposalId: string
  client: WinClientPrefill
  /** Depois de gravado o «ganho» (mesmo que o Odoo tenha falhado). */
  onWon?: () => void
}) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", postal_code: "", nif: "" })
  const [report, setReport] = useState("")
  const [bill, setBill] = useState<File | null>(null)
  const [photos, setPhotos] = useState<File[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<WinResult | null>(null)
  /** Chaves já subidas — «Tentar de novo» reaproveita-as. */
  const [uploaded, setUploaded] = useState<{ bill: WonFile | null; photos: WonFile[]; pdf: WonFile | null } | null>(null)
  const billRequired = kind === "solar"

  useEffect(() => {
    if (!open) return
    setForm({
      name: client.name, email: client.email ?? "", phone: client.phone ?? "",
      address: client.address ?? "", city: client.city ?? "", postal_code: client.postal_code ?? "", nif: "",
    })
    setReport("")
    setBill(null)
    setPhotos([])
    setError(null)
    setResult(null)
    setUploaded(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const nifValid = NIF_REGEX.test(form.nif)
  const canSubmit = !busy && form.name.trim().length > 0 && nifValid && report.trim().length > 0 && (!billRequired || !!bill)

  function pickBill(f: File | null) {
    setError(null)
    if (!f) return setBill(null)
    if (!BILL_TYPES.includes(f.type)) return setError("Fatura de eletricidade: PDF ou imagem.")
    if (f.size > MAX_BYTES) return setError("Fatura de eletricidade acima de 14 MB.")
    setBill(f)
  }

  function addPhotos(list: FileList | null) {
    setError(null)
    const picked = Array.from(list ?? []).filter((f) => PHOTO_TYPES.includes(f.type))
    if (picked.length < (list?.length ?? 0)) setError("Fotos da obra: só imagens (JPEG, PNG, WebP, HEIC).")
    setPhotos((cur) => [...cur, ...picked].slice(0, MAX_PHOTOS))
  }

  /** PDF da proposta (o mesmo do botão PDF) → ficheiro da app. Sem bytes (PDF enorme) segue sem ele. */
  async function proposalPdf(): Promise<WonFile | null> {
    if (!shell) return null
    setBusy("A gerar o PDF da proposta…")
    const res = await shell.client.post<{ success: boolean; data?: { pdf_base64?: string | null }; error?: string }>(
      kind === "solar" ? "actions/solar_export_pdf" : "actions/export_pdf",
      { params: kind === "solar" ? { id: proposalId, variant: "full" } : { id: proposalId } },
    )
    if (!res.success) throw new Error(res.error || "Falha a gerar o PDF da proposta")
    const b64 = res.data?.pdf_base64
    if (!b64) return null
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
    const name = `Proposta ${client.proposal_number ?? ""}.pdf`.replace(/\s+\./, ".")
    setBusy("A enviar o PDF da proposta…")
    const key = await uploadAppFile(shell, new Blob([bytes], { type: "application/pdf" }), name)
    return { key, name, mimetype: "application/pdf" }
  }

  async function upload(): Promise<{ bill: WonFile | null; photos: WonFile[]; pdf: WonFile | null }> {
    if (uploaded) return uploaded
    if (!shell) throw new Error("Sem ligação ao Koortex.")
    if (billRequired && !bill) throw new Error("Falta a fatura de eletricidade.")
    const pdf = await proposalPdf()
    let billOut: WonFile | null = null
    if (bill) {
      setBusy("A enviar a fatura…")
      const billKey = await uploadAppFile(shell, bill, bill.name || "fatura.pdf")
      billOut = { key: billKey, name: bill.name || "fatura.pdf", mimetype: bill.type }
    }
    const out: WonFile[] = []
    for (const [i, p] of photos.entries()) {
      setBusy(`A enviar a foto ${i + 1} de ${photos.length}…`)
      const small = await shrinkPhoto(p)
      if (small.blob.size > MAX_BYTES) throw new Error(`${p.name}: acima de 14 MB.`)
      out.push({ key: await uploadAppFile(shell, small.blob, small.name), name: small.name, mimetype: small.mimetype })
    }
    const done = { bill: billOut, photos: out, pdf }
    setUploaded(done)
    return done
  }

  async function submit() {
    if (!shell) return
    setError(null)
    try {
      const files = await upload()
      setBusy("A fechar o negócio no Odoo…")
      const res = await shell.client.post<{ success: boolean; data?: WinResult; message?: string; error?: string }>(
        "actions/win_proposal",
        {
          params: {
            proposal_id: proposalId,
            client: {
              name: form.name.trim(), vat: form.nif,
              email: form.email.trim() || null, phone: form.phone.trim() || null,
              street: form.address.trim() || null, city: form.city.trim() || null, zip: form.postal_code.trim() || null,
              country_code: "PT",
            },
            report: report.trim(),
            bill: files.bill,
            proposal_pdf: files.pdf,
            photos: files.photos,
          },
        },
      )
      if (!res.success || !res.data) throw new Error(res.error || "Falha a fechar o negócio")
      setResult(res.data)
      shell.bridge.toast({ variant: res.data.odoo_error ? "error" : "success", description: res.message ?? "Negócio ganho" })
      onWon?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro desconhecido")
    } finally {
      setBusy(null)
    }
  }

  const field = (key: keyof typeof form, label: string, opts: { required?: boolean; type?: string } = {}) => (
    <div className="space-y-1">
      <Label required={opts.required}>{label}</Label>
      <Input type={opts.type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} disabled={!!busy || !!uploaded} />
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v) }}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {result ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Trophy className="h-5 w-5 text-emerald-600" />}
            {result ? "Negócio ganho" : "Fechar negócio"}
          </DialogTitle>
          <DialogDescription>
            {result
              ? "A proposta fica ganha e deixa de poder ser editada."
              : <>Para marcar como ganho: dados do cliente e relatório{billRequired ? ", fatura de eletricidade" : ""}. O PDF da proposta, a fatura e as fotos vão para o negócio no Odoo.{client.proposal_number ? <> Proposta <span className="font-mono">{client.proposal_number}</span>.</> : null}</>}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-2 text-sm">
            <p>Encomenda no Odoo: <strong>{result.odoo_order_name || result.odoo_order_id ? odooOrderLabel(result.odoo_order_name, result.odoo_order_id) : "—"}</strong></p>
            <p>Anexos na oportunidade: <strong>{result.attached}</strong>{result.note_posted ? " · relatório na conversa" : ""}</p>
            {error && <p className="text-sm text-red-700 dark:text-red-400">{error}</p>}
            {result.odoo_error && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
                {result.odoo_error}
                {result.pending_files.length > 0 && <> — por enviar: {result.pending_files.join(", ")}</>}
              </div>
            )}
          </div>
        ) : (
          <div className="grid gap-3">
            {error && (
              <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">{error}</div>
            )}
            {field("name", "Nome / Empresa", { required: true })}
            <div className="space-y-1">
              <Label required>NIF / NIPC</Label>
              <Input
                inputMode="numeric" maxLength={9} placeholder="123456789" value={form.nif}
                disabled={!!busy || !!uploaded}
                onChange={(e) => setForm({ ...form, nif: e.target.value.replace(/\D/g, "") })}
                className={form.nif.length > 0 && !nifValid ? "border-red-400 focus-visible:ring-red-400" : ""}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">{field("email", "Email", { type: "email" })}{field("phone", "Telefone")}</div>
            {field("address", "Morada")}
            <div className="grid grid-cols-2 gap-3">{field("city", "Cidade")}{field("postal_code", "Código postal")}</div>

            <div className="space-y-1">
              <Label required>Relatório</Label>
              <textarea
                className="flex min-h-[110px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                placeholder="O que ficou combinado com o cliente, condições da obra, observações para a instalação…"
                value={report} maxLength={5000} disabled={!!busy || !!uploaded}
                onChange={(e) => setReport(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label required={billRequired}>Fatura de eletricidade{billRequired ? "" : " (opcional)"}</Label>
              {bill ? (
                <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="truncate">{bill.name}</span>
                  {!uploaded && !busy && (
                    <button type="button" className="ml-auto text-muted-foreground hover:text-foreground" aria-label="Remover" onClick={() => setBill(null)}>
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ) : (
                <Input type="file" accept={BILL_TYPES.join(",")} onChange={(e) => pickBill(e.target.files?.[0] ?? null)} />
              )}
              <p className="text-xs text-muted-foreground">PDF ou foto da fatura.</p>
            </div>

            <div className="space-y-1">
              <Label>Imagens da obra</Label>
              {photos.length > 0 && (
                <ul className="space-y-1">
                  {photos.map((p, i) => (
                    <li key={`${p.name}-${i}`} className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm">
                      <ImagePlus className="h-4 w-4 text-muted-foreground" />
                      <span className="truncate">{p.name}</span>
                      {!uploaded && !busy && (
                        <button type="button" className="ml-auto text-muted-foreground hover:text-foreground" aria-label="Remover" onClick={() => setPhotos((cur) => cur.filter((_, j) => j !== i))}>
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {photos.length < MAX_PHOTOS && !uploaded && (
                <Input type="file" multiple accept={PHOTO_TYPES.join(",")} onChange={(e) => { addPhotos(e.target.files); e.target.value = "" }} />
              )}
              <p className="text-xs text-muted-foreground">Opcional, até {MAX_PHOTOS}. As fotos grandes são reduzidas antes de enviar.</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {result ? (
            <>
              {result.odoo_error && (
                <Button variant="outline" onClick={submit} disabled={!!busy}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {busy ?? "Tentar de novo no Odoo"}
                </Button>
              )}
              <Button onClick={() => onOpenChange(false)} disabled={!!busy}>Fechar</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={!!busy}>Cancelar</Button>
              <Button onClick={submit} disabled={!canSubmit}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4" />}
                {busy ?? (uploaded ? "Tentar de novo" : "Marcar como ganho")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
